import { InputSuffix, TextBox } from '@progress/kendo-react-inputs';
import { HeroIcon } from '../shared/HeroIcon.tsx';
import { CategorySiteTree } from './CategorySiteTree.tsx';
import { PublicCompanyData } from './contexts/PublicCompanyProvider.tsx';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

interface PublicMenuSidebarProps {
  publicCompany?: PublicCompanyData;
  hideCategories?: boolean;
  hideSites?: boolean;
  hideSearch?: boolean;
}

export const PublicMenuSidebar: React.FC<PublicMenuSidebarProps> = ({
  publicCompany,
  hideCategories,
  hideSites,
  hideSearch = false,
}) => {
  const searchParameterFromUrl = useLocation({
    select: (l) => new URLSearchParams(l.searchStr ?? ''),
  });
  const [searchInputValue, setSearchInputValue] = useState<string>(
    searchParameterFromUrl.get('search') || '',
  );
  const navigate = useNavigate();
  const searchTargetPath = '/?search=' + encodeURIComponent(searchInputValue);

  useEffect(() => {
    if (!searchParameterFromUrl) {
      // Clear local input when URL search param is removed by navigation.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSearchInputValue('');
    }
  }, [searchParameterFromUrl]);

  return (
    <aside className="order-first col-span-1 flex flex-col gap-6 md:order-none md:col-span-3">
      {/* Show search box only when search is active (but never when the /search advanced view is shown */}
      {!hideSearch && publicCompany?.functionalSettings.searchActive && (
        <>
          <h2>Suche</h2>
          <div className="flex flex-col gap-1">
            <TextBox
              placeholder="Suchbegriff"
              className="!border-gray-200 *:font-inter *:text-base [&_.k-input-inner]:outline-none"
              value={searchInputValue}
              onChange={(e) => {
                setSearchInputValue(String(e.value));
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  navigate({ to: searchTargetPath as any });
                }
              }}
              suffix={() => (
                <InputSuffix
                  className={`!bg-[var(--kendo-color-base)] hover:!bg-[var(--kendo-color-base-hover)]`}
                >
                  <Link
                    className={`flex size-full items-center justify-center px-2 ${
                      !searchInputValue
                        ? 'pointer-events-none size-full cursor-default'
                        : 'cursor-pointer'
                    }`}
                    to={searchTargetPath}
                  >
                    <HeroIcon name="MagnifyingGlass" className="h-5 w-5" />
                  </Link>
                </InputSuffix>
              )}
              size={'large'}
            />
            <Link className="self-end text-xs hover:underline" to="/search">
              Zur erweiterten Suche
            </Link>
          </div>
        </>
      )}

      {publicCompany &&
        (Boolean(publicCompany?.sites.length) ||
          Boolean(publicCompany?.categories.length)) && (
          <>
            <h2 className="hidden md:block">Seminarangebot</h2>
            <CategorySiteTree
              company={publicCompany}
              hideCategories={hideCategories}
              hideSites={hideSites}
            />
          </>
        )}
    </aside>
  );
};
